
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { X, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { newClientEntityId } from '@/utils/ids';
import { GLOBAL_TOAST_EVENT, type GlobalToastDetail } from '@/utils/globalToast';

export type ToastType = 'success' | 'error' | 'info';

interface Toast {
  id: string;
  message: string;
  type: ToastType;
}

interface ToastContextType {
  showToast: (message: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    const id = newClientEntityId();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3000);
  }, []);

  useEffect(() => {
    const onGlobalToast = (event: Event) => {
      const detail = (event as CustomEvent<GlobalToastDetail>).detail;
      if (!detail?.message) return;
      showToast(detail.message, detail.type ?? 'info');
    };
    window.addEventListener(GLOBAL_TOAST_EVENT, onGlobalToast);
    return () => window.removeEventListener(GLOBAL_TOAST_EVENT, onGlobalToast);
  }, [showToast]);

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <div 
        className="fixed bottom-6 right-6 z-50 flex flex-col gap-3"
        aria-live="polite"
        aria-atomic="false"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.type === 'error' ? 'alert' : 'status'}
            aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
            className={`flex items-center p-4 rounded-lg shadow-lg border animate-slide-up ${
              toast.type === 'success' ? 'bg-white dark:bg-slate-800 border-emerald-500 text-emerald-700 dark:text-emerald-400' :
              toast.type === 'error' ? 'bg-white dark:bg-slate-800 border-red-500 text-red-700 dark:text-red-400' :
              'bg-white dark:bg-slate-800 border-blue-500 text-blue-700 dark:text-blue-400'
            }`}
          >
            <div className={`mr-3 p-1 rounded-full ${
               toast.type === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/30' :
               toast.type === 'error' ? 'bg-red-100 dark:bg-red-900/30' : 'bg-blue-100 dark:bg-blue-900/30'
            }`} aria-hidden="true">
              {toast.type === 'success' && <CheckCircle size={16} />}
              {toast.type === 'error' && <AlertCircle size={16} />}
              {toast.type === 'info' && <Info size={16} />}
            </div>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100 mr-8">{toast.message}</p>
            <button 
              onClick={() => removeToast(toast.id)}
              aria-label="Close notification"
              className="ml-auto text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
            >
              <X size={14} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) throw new Error('useToast must be used within a ToastProvider');
  return context;
};
